
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2 } from 'lucide-react';
import { RequestImageUpload } from '@/components/services/RequestImageUpload';
import { ProviderSelector } from '@/components/ProviderSelector';
import { useAuth } from '@/contexts/AuthContext';

const requestFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  service_category_id: z.string().min(1, 'Category is required'),
  budget: z.string().optional().refine((val) => !val || parseFloat(val) >= 5, {
    message: "Budget must be at least $5",
  }),
});

type RequestFormData = z.infer<typeof requestFormSchema>;

interface ServiceCategory {
  id: string;
  name: string;
}

const FORM_STORAGE_KEY = 'request_form_draft';

export const RequestForm = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm<RequestFormData>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      title: '',
      description: '',
      service_category_id: '',
      budget: '',
    },
  });

  // Restore form state from sessionStorage on mount
  useEffect(() => {
    const savedData = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        form.reset({
          title: parsed.title || '',
          description: parsed.description || '',
          service_category_id: parsed.service_category_id || '',
          budget: parsed.budget || '',
        });
        setImageUrl(parsed.imageUrl || null);
        setSelectedProviderId(parsed.selectedProviderId || null);
        // Clear storage after restoring
        sessionStorage.removeItem(FORM_STORAGE_KEY);
      } catch (e) {
        console.error('Error restoring form state:', e);
      }
    }
  }, []);

  // Save form state to sessionStorage whenever it changes
  const saveFormState = () => {
    const values = form.getValues();
    const state = {
      ...values,
      imageUrl,
      selectedProviderId,
    };
    sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(state));
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name');

      if (error) {
        console.error('Error fetching categories:', error);
        toast({
          title: 'Error',
          description: 'Failed to load service categories',
          variant: 'destructive',
        });
        return;
      }

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load service categories',
        variant: 'destructive',
      });
    }
  };

  const onSubmit = async (data: RequestFormData) => {
    setIsLoading(true);
    
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        toast({
          title: 'Error',
          description: 'You must be logged in to submit a request',
          variant: 'destructive',
        });
        return;
      }

      // If a provider is selected, create a direct conversation instead of public request
      if (selectedProviderId) {
        // Check if conversation already exists with this provider
        const { data: existingConvo } = await supabase
          .from('conversations')
          .select('id')
          .or(`and(participant1_id.eq.${authUser.id},participant2_id.eq.${selectedProviderId}),and(participant1_id.eq.${selectedProviderId},participant2_id.eq.${authUser.id})`)
          .maybeSingle();

        let conversationId: string;

        if (existingConvo) {
          conversationId = existingConvo.id;
        } else {
          // Create a new conversation with the budget as the proposed price
          const budgetAmount = data.budget ? parseFloat(data.budget) : null;
          const initialMessage = `Hi! I have a request for you:\n\n**${data.title}**\n\n${data.description}${budgetAmount ? `\n\nBudget: $${budgetAmount}` : ''}`;

          const { data: newConvo, error: convoError } = await supabase
            .from('conversations')
            .insert({
              participant1_id: authUser.id,
              participant2_id: selectedProviderId,
              last_message: initialMessage,
              // Set the budget as the proposed price so it shows in the header
              latest_proposal_price: budgetAmount,
              latest_proposal_pricing_type: 'fixed',
              latest_proposal_by: authUser.id,
            })
            .select('id')
            .single();

          if (convoError || !newConvo) {
            throw new Error('Failed to create conversation');
          }

          conversationId = newConvo.id;

          // Send the initial message
          const { error: msgError } = await supabase
            .from('messages')
            .insert({
              conversation_id: conversationId,
              sender_id: authUser.id,
              text: initialMessage,
              file_url: imageUrl,
              file_type: imageUrl ? 'image' : null,
            });

          if (msgError) {
            throw new Error('Failed to send message');
          }

          // If a budget was set, send a price proposal message so the provider can confirm it
          if (budgetAmount) {
            await supabase
              .from('messages')
              .insert({
                conversation_id: conversationId,
                sender_id: authUser.id,
                text: `[PRICE_PROPOSAL] Proposed price: $${budgetAmount}`,
              });
          }
        }

        toast({
          title: 'Request sent!',
          description: 'Your request has been sent directly to the provider.',
        });
        
        navigate(`/chat/${conversationId}`);
        return;
      }

      // Standard public request flow
      const requestData = {
        title: data.title,
        description: data.description,
        service_category_id: data.service_category_id,
        pricing_type: 'fixed',
        budget: data.budget ? parseFloat(data.budget) : null,
        image_url: imageUrl,
        user_id: authUser.id,
      };

      const { data: insertedRequest, error } = await supabase
        .from('service_requests')
        .insert([requestData])
        .select('id')
        .single();

      if (error) {
        console.error('Error submitting request:', error);
        toast({
          title: 'Error',
          description: 'Failed to submit request. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      // Show success message
      setShowSuccess(true);
      
      // Navigate to requests page after a short delay
      setTimeout(() => {
        navigate(`/requests?highlight=${insertedRequest.id}`);
      }, 2500);

      form.reset();
      setImageUrl(null);
      setSelectedProviderId(null);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <AlertTitle className="text-green-800 text-xl font-semibold">
            Request Submitted Successfully!
          </AlertTitle>
          <AlertDescription className="text-green-700 mt-2">
            Your service request has been submitted and is now visible to service providers. 
            You'll be redirected to view all requests shortly...
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Submit a Service Request</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="service_category_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Category</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter request title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Describe your service request..."
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <RequestImageUpload
            currentImageUrl={imageUrl}
            onImageChange={setImageUrl}
          />

          <FormField
            control={form.control}
            name="budget"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budget (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder="Enter your budget"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Provider selector - optional direct send */}
          {user && (
            <ProviderSelector
              selectedProviderId={selectedProviderId}
              onProviderChange={setSelectedProviderId}
              currentUserId={user.id}
              onBeforeNavigate={saveFormState}
            />
          )}

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? 'Submitting...' : selectedProviderId ? 'Send to Provider' : 'Submit Request'}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default RequestForm;
